import { Component, EventEmitter, Inject, Input, LOCALE_ID, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Router } from '@angular/router';
import { ItemToSell, ItemToSellSource, ItemToSellState } from 'src/app/_services';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'item-list',
  templateUrl: './item-list.component.html',
  styleUrls: ['./item-list.component.scss']
})
export class ItemListComponent implements OnInit, OnChanges {
  
  @Input() items: ItemToSell[] = [];
  @Input() orderBy: ('dateOfPurchase' | 'purchasePriceEUR' | 'state')[] = ['dateOfPurchase'];
  @Input() enableButtons: boolean = false;
  @Input() showPurchasePrice: boolean = true;
  @Input() showPrice: boolean = false;
  @Output() onDelete: EventEmitter<ItemToSell> = new EventEmitter();
  
  picturesUrl = environment.picturesUrl;
  columnsToDisplay = ['titleAndImage', 'purchasePrice'];
  needsHeader = false;
  
  constructor(
    private breakpointObserver: BreakpointObserver, 
    private router: Router,
    @Inject(LOCALE_ID) private locale: string) {
      
  }
  
  ngOnChanges(changes: SimpleChanges): void {
    
    if (changes['items'] || changes['orderBy']) {
      this.sortItems();
    }
  }
  
  private sortItems() {
    
    this.orderBy.forEach(orderBy => {
      
      console.log("Sorting items by " + orderBy);
      
      this.items.sort((a, b) => {
        switch (orderBy) {
          case 'purchasePriceEUR':
            return b.purchasePriceEUR!.subtract(a.purchasePriceEUR!).toNumber();
          case 'state':
            if (a.state === ItemToSellState.LISTED && b.state !== ItemToSellState.LISTED) {
              return -1;
            } else if (b.state === ItemToSellState.LISTED && a.state !== ItemToSellState.LISTED) {
              return 1;
            } else {
              return 0;
            }
          default:
            return (b.dateOfPurchase ? b.dateOfPurchase.getTime() : b.createdTime!.getTime()) - 
                (a.dateOfPurchase ? a.dateOfPurchase.getTime() : a.createdTime!.getTime());
        }
      });
    });
  }
  
  ngOnInit(): void {
    
    this.sortItems();
    
    this.breakpointObserver.observe([
      Breakpoints.XSmall
    ]).subscribe(result => {
      
      if (result.matches) {
        let columns = ['image', 'xsDetails'];
        this.calculateIfHeaderIsNeeded();
        this.columnsToDisplay = columns;
      }
    });
    
    this.breakpointObserver.observe([
      Breakpoints.Small
    ]).subscribe(result => {
      
      if (result.matches) {
        let columns = ['image', 'title'];
        this.addPurchasePriceColumnIfNeeded(columns);
        this.calculateIfHeaderIsNeeded();
        this.addCreateListingColumnIfNeeded(columns);
        this.columnsToDisplay = columns
      }
    });
    
    this.breakpointObserver.observe([
      Breakpoints.Small, Breakpoints.XSmall
    ]).subscribe(result => {
      
      if (!result.matches) {
        let columns = ['image', 'title'];
        this.addPurchasePriceColumnIfNeeded(columns);
        this.calculateIfHeaderIsNeeded();
        this.addCreateListingColumnIfNeeded(columns);
        this.columnsToDisplay = columns;
      }
    });
  }
  
  private addPurchasePriceColumnIfNeeded(columns: string[]) {
    if (this.showPurchasePrice && columns.indexOf('purchasePrice') === -1) {
      columns.push('purchasePrice')
    }
  }
  
  private addCreateListingColumnIfNeeded(columns: string[]) {
    if (this.areButtonsNeeded && columns.indexOf('createListing') === -1) {
      columns.push('createListing')
    }
  }
  
  get editOnClick(): boolean {
    return this.enableButtons;
  }
  
  get areButtonsNeeded(): boolean {
    return this.enableButtons;
  }
  
  private calculateIfHeaderIsNeeded() {
    this.needsHeader = false;
  }
  
  getFillerIconNameForItem(item: ItemToSell): string {
    return item.fillerIconName;
  }
  
  createListing(item: ItemToSell) {
    
    if (this.goodEnoughToSkipCreateListingPage(item)) {
      this.router.navigate(["/view-listing/" + item.id], { state: { flow: '/items-to-sell' }});
    } else {
      this.router.navigate(['/create-listing/' + item.id]);
    }
  }
  
  private goodEnoughToSkipCreateListingPage(item: ItemToSell): boolean {
    
    if (item.condition === undefined) {
      return false;
    }
    
    if (item.pictures.length === 0) {
      return false;
    }
    
    if (item.source === ItemToSellSource.MAILBOX) {
      return false;
    }
    
    return true;
  }
  
  editListing(itemId: number) {
    this.router.navigate(['/edit-listing/' + itemId]);
  }
  
  viewListing(itemId: number) {
    this.router.navigate(['/view-listing/' + itemId]);
  }
  
  getItemURL(item: ItemToSell): string {
    return `${environment["baseURL"]}/${this.locale}/view-listing/${item.id}`;
  }
  
  public deleteItem(item: ItemToSell) {
    this.onDelete.emit(item);
  }

  public isDraft(item: ItemToSell): boolean {
    return item.state === ItemToSellState.DRAFT;
  }
  
  public isReadyForLisitng(item: ItemToSell): boolean {
    return item.state === ItemToSellState.READY_FOR_LISTING;
  }
  
  public isListed(item: ItemToSell): boolean {
    return item.state === ItemToSellState.LISTED;
  }
}
